/**
 * Error Logging Service
 *
 * Centralized error logging with context including user info, action,
 * stack trace, component, and timestamp. Stores errors in memory and
 * can be extended to send to external logging services.
 */

export type ErrorSeverity = 'error' | 'warning' | 'info';

export interface ErrorLogEntry {
  id: string;
  timestamp: string;
  message: string;
  stack?: string;
  component?: string;
  action?: string;
  userId?: string;
  severity: ErrorSeverity;
  metadata?: Record<string, unknown>;
}

export interface LogErrorParams {
  message: string;
  stack?: string;
  component?: string;
  action?: string;
  severity?: ErrorSeverity;
  metadata?: Record<string, unknown>;
}

const MAX_LOG_ENTRIES = 100;

class ErrorLoggingService {
  private logs: ErrorLogEntry[] = [];
  private userId: string | null = null;
  private listeners: Array<(entry: ErrorLogEntry) => void> = [];

  /**
   * Set the current user ID for error context
   */
  setUserId(userId: string | null): void {
    this.userId = userId;
  }

  /**
   * Log an error with full context
   */
  logError(params: LogErrorParams): ErrorLogEntry {
    const entry: ErrorLogEntry = {
      id: `err-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      timestamp: new Date().toISOString(),
      message: params.message,
      stack: params.stack,
      component: params.component,
      action: params.action,
      userId: this.userId || undefined,
      severity: params.severity || 'error',
      metadata: params.metadata,
    };

    this.logs.push(entry);

    // Keep log size bounded
    if (this.logs.length > MAX_LOG_ENTRIES) {
      this.logs = this.logs.slice(-MAX_LOG_ENTRIES);
    }

    // Notify listeners
    this.listeners.forEach((listener) => listener(entry));

    // Console output for development
    if (import.meta.env.DEV) {
      const logMethod =
        params.severity === 'error' ? 'error' : params.severity === 'warning' ? 'warn' : 'info';
      console[logMethod](`[ErrorLogging] ${entry.severity.toUpperCase()}: ${entry.message}`, {
        component: entry.component,
        action: entry.action,
        userId: entry.userId,
        timestamp: entry.timestamp,
      });
    }

    return entry;
  }

  /**
   * Log a warning
   */
  logWarning(params: Omit<LogErrorParams, 'severity'>): ErrorLogEntry {
    return this.logError({ ...params, severity: 'warning' });
  }

  /**
   * Log an info message
   */
  logInfo(params: Omit<LogErrorParams, 'severity'>): ErrorLogEntry {
    return this.logError({ ...params, severity: 'info' });
  }

  /**
   * Get all logged errors
   */
  getLogs(): ErrorLogEntry[] {
    return [...this.logs];
  }

  /**
   * Get errors filtered by severity
   */
  getLogsBySeverity(severity: ErrorSeverity): ErrorLogEntry[] {
    return this.logs.filter((log) => log.severity === severity);
  }

  /**
   * Get recent errors (last N entries)
   */
  getRecentLogs(count: number = 10): ErrorLogEntry[] {
    return this.logs.slice(-count);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Subscribe to new error log entries
   */
  subscribe(listener: (entry: ErrorLogEntry) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Get the total count of logged errors
   */
  getErrorCount(): number {
    return this.logs.filter((log) => log.severity === 'error').length;
  }
}

// Singleton instance
export const errorLoggingService = new ErrorLoggingService();
